import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, Check, ClipboardList, Download, Loader2, MessagesSquare, School as SchoolIcon, Search, Send, Share2 } from "lucide-react";
import { LogoMark } from "@/components/Navbar";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-pine-900/15 bg-white px-4 py-3 text-sm text-pine-950 outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-stone-400 focus:border-pine-700 focus:ring-2 focus:ring-pine-700/15";

const PROGRAMMES = [
  "Screening Programme",
  "Individual Therapy",
  "Group Therapy",
  "Teacher Training",
  "Parent Counselling",
  "Full partnership",
  "Not sure yet",
];

const MODEL = [
  { icon: Search, title: "Screen", text: "Identify students who need support — quietly, up to 10 per visit." },
  { icon: MessagesSquare, title: "Support", text: "Individualised therapy in your school — group or one-to-one." },
  { icon: ClipboardList, title: "Report", text: "Clear monthly progress reports for parents and management." },
];

const HIGHLIGHTS = [
  "RCI-registered — full compliance, Reg. No. A58697",
  "In-school delivery — no travel for parents, no lost class time",
  "Team model — coordinator, assessor, and therapist",
  "Data protection — secure records, written parent consent",
  "Flexible — shaped to your timetable and budget",
];

const EMPTY = { name: "", email: "", phone: "", schoolName: "", studentCount: "", programme: "", message: "" };

export default function Schools() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid =
    form.name.trim() && /\S+@\S+\.\S+/.test(form.email) && form.phone.trim() &&
    form.schoolName.trim() && form.studentCount.trim() && form.programme;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/school-enquiries`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        school_name: form.schoolName,
        student_count: form.studentCount,
        programme: form.programme,
        message: form.message,
      });
      toast.success("Enquiry sent", { description: "Sameeksha will call you within one business day." });
      setForm(EMPTY);
    } catch {
      toast.error("Couldn't send your enquiry", { description: "Please try again or email spellingbeetherapy@gmail.com." });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream text-pine-900" data-testid="schools-page">
      <header className="border-b border-pine-900/10">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-5 sm:px-8">
          <Link to="/" className="flex items-center gap-2.5" data-testid="schools-brand">
            <LogoMark />
            <span className="leading-none">
              <span className="block font-serif text-lg font-semibold tracking-tight">SpellingBee</span>
              <span className="mt-0.5 block font-mono text-[9px] uppercase tracking-[0.22em] text-pine-700/70">Therapy Services</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="schools-back-link"
            className="flex items-center gap-1.5 text-sm font-semibold text-pine-700 transition-colors hover:text-clay"
          >
            <ArrowLeft size={15} /> Back to site
          </Link>
        </div>
      </header>

      <main>
        <section className="mx-auto max-w-7xl px-5 pb-10 pt-16 sm:px-8 sm:pt-20">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <p className="mb-4 font-mono text-xs font-medium uppercase tracking-[0.28em] text-pine-700">For Schools</p>
            <h1 className="max-w-3xl font-serif text-4xl font-semibold leading-[1.08] tracking-tight text-pine-950 sm:text-5xl" data-testid="schools-headline">
              Speech & language therapy, delivered in your school.
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-stone-600 sm:text-lg">
              RCI-registered therapy for your students, without adding work to
              your staff. No travel for parents. No disruption for you.
            </p>
            <p className="mt-4 inline-block rounded-full border border-honey/60 bg-honey/20 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-pine-900">
              RCI Reg. No. A58697
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="/assets/spellingbee-brochure.pdf"
                download="SpellingBee-Therapy-Services-Brochure.pdf"
                data-testid="schools-brochure-download"
                className="flex items-center gap-2 rounded-full bg-pine-900 px-7 py-3.5 text-sm font-semibold text-cream transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-pine-800"
              >
                <Download size={15} /> Download brochure (PDF)
              </a>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(
                  "SpellingBee Therapy Services — RCI-registered speech & language therapy, delivered in school (Reg. No. A58697). Here's the brochure: " +
                    window.location.origin + "/assets/spellingbee-brochure.pdf"
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="schools-brochure-share"
                className="flex items-center gap-2 rounded-full bg-honey px-7 py-3.5 text-sm font-semibold text-pine-950 transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#ffd95e]"
              >
                <Share2 size={15} /> Share on WhatsApp
              </a>
            </div>
          </motion.div>
        </section>

        <section className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {MODEL.map((m, i) => (
              <Reveal key={m.title} delay={i * 0.08}>
                <div className="flex h-full items-start gap-4 rounded-3xl border border-pine-900/10 bg-white p-7">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-honey/25 text-pine-900">
                    <m.icon size={18} />
                  </span>
                  <div>
                    <h3 className="font-serif text-lg font-semibold text-pine-950">{m.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-stone-600">{m.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 py-20 sm:px-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="mb-4 font-mono text-xs font-medium uppercase tracking-[0.28em] text-pine-700">Why schools choose us</p>
            <h2 className="font-serif text-2xl font-semibold tracking-tight text-pine-950 sm:text-3xl">
              Built for schools, not adapted to them.
            </h2>
            <ul className="mt-8 space-y-5">
              {HIGHLIGHTS.map((h) => (
                <li key={h} className="flex items-start gap-3 text-sm leading-relaxed text-stone-700">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-honey text-pine-950">
                    <Check size={12} strokeWidth={3} />
                  </span>
                  {h}
                </li>
              ))}
            </ul>
            <p className="mt-10 font-serif text-xl font-semibold italic text-pine-900">Small steps. Big progress.</p>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={0.1}>
            <form
              onSubmit={submit}
              data-testid="school-enquiry-form"
              className="rounded-3xl border border-pine-900/10 bg-sand p-8 sm:p-10"
            >
              <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-clay">
                <SchoolIcon size={14} /> Partnership enquiry
              </p>
              <h3 className="mt-3 font-serif text-xl font-semibold text-pine-950 sm:text-2xl">
                Tell us about your school.
              </h3>
              <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <input data-testid="school-form-name" className={inputCls} placeholder="Contact person name" value={form.name} onChange={set("name")} />
                <input data-testid="school-form-email" className={inputCls} type="email" placeholder="Email" value={form.email} onChange={set("email")} />
                <input data-testid="school-form-phone" className={inputCls} placeholder="Phone" value={form.phone} onChange={set("phone")} />
                <input data-testid="school-form-school-name" className={inputCls} placeholder="School name" value={form.schoolName} onChange={set("schoolName")} />
                <input data-testid="school-form-student-count" className={inputCls} placeholder="Approx. student count" value={form.studentCount} onChange={set("studentCount")} />
                <select data-testid="school-form-programme" className={inputCls} value={form.programme} onChange={set("programme")}>
                  <option value="">Programme interest…</option>
                  {PROGRAMMES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <textarea
                data-testid="school-form-message"
                className={`${inputCls} mt-4 min-h-[110px] resize-none`}
                placeholder="Anything else we should know? (optional)"
                value={form.message}
                onChange={set("message")}
              />
              <button
                data-testid="school-form-submit"
                type="submit"
                disabled={!valid || sending}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-pine-900 py-4 text-sm font-semibold text-cream transition-[background-color,opacity,transform] duration-300 hover:-translate-y-0.5 hover:bg-pine-800 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto sm:px-10"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
                {sending ? "Sending…" : "Send enquiry"}
              </button>
            </form>
          </Reveal>
        </section>
      </main>
    </div>
  );
}
